import { Fragment, useCallback, useEffect, useId, useRef, useState, type CSSProperties } from 'react'
import parse from 'html-react-parser'
import { ArrowSimple, Copy, t } from '@stokr/components-library'
import CrossIcon from '@stokr/components-library/dist/images/cross-icon.svg?react'
import { Section } from 'components/sto/Section'
import { SectionHeading } from 'components/sto/Section/Section.styles'
import {
  DrawerBody,
  DrawerHeader,
  RoundButton,
  StoSideModal,
  ViewAllButton,
} from 'components/sto/Documents/Documents.styles'
import { useUpdates, type StoUpdate } from 'hooks/sto/useUpdates'
import { useSeenOnce, useStoTrack } from 'hooks/sto/useStoTracking'
import {
  Actions,
  Body,
  Dot,
  LinkOut,
  List,
  MoreToggle,
  Row,
  RowText,
  Rule,
  Text,
  Title,
  TitleLine,
  UpdateDate,
  VisuallyHidden,
} from './Updates.styles'

/** Rows on the card (Figma 4582:136587); the rest open in the drawer. */
const PREVIEW_COUNT = 5

const isPdf = (url: string) => /\.pdf($|[?#])/i.test(url)

/** Lines of an update's text before "Show more". */
const TEXT_LINES = 4

interface UpdateRowProps {
  update: StoUpdate
  index: number
  location: 'card' | 'drawer'
}

/**
 * One update. Its text stops at TEXT_LINES lines, fading out, with "Show more" when it is cut: one
 * long announcement must not push the rest of the list away. The server renders it clamped; the
 * toggle appears once the browser has measured that there is more.
 */
const UpdateRow = ({ update, index, location }: UpdateRowProps) => {
  const trackSto = useStoTrack()
  const textRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [overflows, setOverflows] = useState(false)

  useEffect(() => {
    const element = textRef.current
    if (!element || expanded || typeof ResizeObserver === 'undefined') return undefined
    const measure = () => setOverflows(element.scrollHeight > element.clientHeight + 1)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [expanded])

  return (
    <Row data-cy={`sto-update-${index}`}>
      <RowText>
        <TitleLine>
          {/* Logged out: the newest only. Signed in: also every update since the last visit, pulsing. */}
          {(update.isLatest || update.isUnseen) && <Dot $blink={update.isUnseen} aria-hidden="true" />}
          <Title>{update.title}</Title>
          {update.isUnseen && (
            <VisuallyHidden>
              <Copy id="stoPage.updates.new" />
            </VisuallyHidden>
          )}
        </TitleLine>
        {update.text && (
          <Text
            ref={textRef}
            $clamped={!expanded}
            $fade={!expanded && overflows}
            style={{ '--update-lines': TEXT_LINES } as CSSProperties}>
            {parse(update.text)}
          </Text>
        )}
        {(overflows || update.link) && (
          <Actions>
            {overflows && (
              <MoreToggle
                type="button"
                aria-expanded={expanded}
                data-cy={`sto-update-more-${index}`}
                onClick={() => {
                  if (!expanded) trackSto('sto_update_expanded', { index, location })
                  setExpanded(!expanded)
                }}>
                <Copy id={expanded ? 'stoPage.updates.showLess' : 'stoPage.updates.showMore'} />
              </MoreToggle>
            )}
            {update.link && (
              <LinkOut
                href={update.link}
                target="_blank"
                rel="noopener noreferrer"
                data-cy={`sto-update-link-${index}`}
                onClick={() => trackSto('sto_update_link_clicked', { index, location, pdf: isPdf(update.link ?? '') })}>
                <Copy id={isPdf(update.link) ? 'stoPage.updates.openPdf' : 'stoPage.updates.openLink'} />
              </LinkOut>
            )}
          </Actions>
        )}
      </RowText>
      {update.dateLabel && <UpdateDate>{update.dateLabel}</UpdateDate>}
    </Row>
  )
}

const UpdateRows = ({ updates, location }: { updates: StoUpdate[]; location: 'card' | 'drawer' }) => (
  <List>
    {updates.map((update, index) => (
      <Fragment key={update.id}>
        <UpdateRow update={update} index={index} location={location} />
        <Rule />
      </Fragment>
    ))}
  </List>
)

/** Figma 4582:136587: the newest updates in a card, the whole list in a side drawer. */
export const Updates = () => {
  const { state, markSeen, canRecord } = useUpdates()
  const trackSto = useStoTrack()
  const rootRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), [])
  useSeenOnce(rootRef, markSeen, canRecord && state.status === 'ready')

  return (
    <Section anchor="updates" title={<Copy id="stoPage.updates.title" />} state={state} collapsible="always">
      {({ updates, unseenCount }) => (
        <Body ref={rootRef}>
          <UpdateRows updates={updates.slice(0, PREVIEW_COUNT)} location="card" />
          {updates.length > PREVIEW_COUNT && (
            <ViewAllButton
              type="button"
              black
              data-cy="sto-updates-view-all"
              aria-haspopup="dialog"
              onClick={() => {
                trackSto('sto_updates_view_all_clicked', { count: updates.length, unseen: unseenCount })
                markSeen()
                setIsDrawerOpen(true)
              }}>
              <Copy id="stoPage.updates.viewFullList" />
              <ArrowSimple direction="right" width={10} height={6} aria-hidden="true" />
            </ViewAllButton>
          )}

          {/* Same drawer as Documents' full list. */}
          <StoSideModal
            isOpen={isDrawerOpen}
            onClose={closeDrawer}
            showCloseButton={false}
            width={0}
            minWidth={704}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}>
            <DrawerBody>
              <DrawerHeader>
                <SectionHeading id={titleId}>
                  <Copy id="stoPage.updates.title" />
                </SectionHeading>
                <RoundButton
                  $size={40}
                  type="button"
                  aria-label={t('stoPage.updates.closeList')}
                  data-cy="sto-updates-drawer-close"
                  onClick={closeDrawer}>
                  <CrossIcon width={12} height={12} aria-hidden="true" />
                </RoundButton>
              </DrawerHeader>
              <UpdateRows updates={updates} location="drawer" />
            </DrawerBody>
          </StoSideModal>
        </Body>
      )}
    </Section>
  )
}
